import { StyledSelect } from "@ando/ui";
/**
 * ChatSyncFiltersSheet — V2 port of V1 ``ChatSyncFilters.tsx`` (May 25, 2026).
 *
 * Unified filter UI for Teams / Slack / Google Chat. The three providers
 * have different entity models (teams+channels vs spaces vs chat types)
 * so the picker block in the middle is provider-conditional, but the
 * surrounding chrome (time range, max conversations, preview, sync
 * button) is shared 1:1.
 *
 * Section order (V1 parity):
 *   1. Header (provider chip + connection name)
 *   2. Time range (7d / 30d / 90d / Custom)
 *   3. Provider-specific picker:
 *      - Teams      → "Chat private & gruppi" + "Canali" toggles
 *                     + Teams list (chip per team) + Channels list
 *      - GoogleChat → "Spazi", "Chat di gruppo", "Messaggi diretti" type chips
 *                     + Spaces list grouped by type
 *      - Slack      → "Chat private" + "Canali" toggles only (no list,
 *                     Slack syncs all matching channels per filter)
 *   4. Advanced (collapsible): Max conversations dropdown
 *      + (Google Chat only) Inactive-N-days exclusion
 *   5. Preview strip (debounced 500ms)
 *   6. Sync button (disabled when count=0 or validation fail)
 */

import type {
  ChatProvider,
  ChatSyncFilters,
  GoogleChatSpaceType,
} from "@ando/core";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  cn,
  toast,
} from "@ando/ui";
import {
  ChevronDown,
  Filter,
  Hash,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";

import {
  useChatPreviewSync,
  useChatSpaces,
  useChatSyncStatus,
  useChatSyncTrigger,
  useChatTeams,
} from "@/features/sync/hooks/useChatSync";

import { GoogleMeetTranscriptImport } from "./GoogleMeetTranscriptImport";

// ──────────────────────────────────────────────────────────────────────
// Constants (V1 parity)
// ──────────────────────────────────────────────────────────────────────

const MAX_CUSTOM_RANGE_DAYS = 90;
const PREVIEW_DEBOUNCE_MS = 500;
const MAX_CONV_OPTIONS = [100, 250, 500, 1000] as const;
const INACTIVE_OPTIONS: ReadonlyArray<{
  readonly value: number;
  readonly label: MessageDescriptor;
}> = [
  { value: 0, label: msg`Tutti gli spazi` },
  { value: 7, label: msg`Inattivi 7+ giorni esclusi` },
  { value: 30, label: msg`Inattivi 30+ giorni esclusi` },
  { value: 90, label: msg`Inattivi 90+ giorni esclusi` },
];

const DEFAULT_FILTERS: ChatSyncFilters = {
  time_range: "30d",
  max_conversations: 500,
  include_private_chats: true,
  include_channels: true,
};

const PROVIDER_LABEL: Readonly<Record<ChatProvider, string>> = {
  teams: "Microsoft Teams",
  google_chat: "Google Chat",
  slack: "Slack",
};

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function useDebouncedEffect(
  effect: React.EffectCallback,
  deps: React.DependencyList,
  delayMs: number,
): void {
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      effect();
    }, delayMs);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function diffDays(a: string, b: string): number {
  return Math.floor(
    (new Date(b).getTime() - new Date(a).getTime()) / 86_400_000,
  );
}

function validateCustomRange(
  after: string,
  before: string,
  i18n: I18n,
): string | null {
  if (!after || !before) return null;
  if (new Date(before) <= new Date(after)) {
    return i18n._(
      msg`Intervallo date non valido (fine deve essere dopo l'inizio).`,
    );
  }
  if (diffDays(after, before) > MAX_CUSTOM_RANGE_DAYS) {
    return i18n._(
      msg`L'intervallo personalizzato non può superare ${MAX_CUSTOM_RANGE_DAYS} giorni.`,
    );
  }
  return null;
}

// ──────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────

export interface ChatSyncFiltersSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly provider: ChatProvider;
  /** Optional Slack multi-workspace key. */
  readonly connectionKey?: string;
  readonly displayName?: string;
  readonly initialFilters?: ChatSyncFilters;
  readonly onSyncStarted?: (jobId: string | null) => void;
}

export const ChatSyncFiltersSheet: React.FC<ChatSyncFiltersSheetProps> = ({
  open,
  onOpenChange,
  provider,
  connectionKey,
  displayName,
  initialFilters,
  onSyncStarted,
}) => {
  const { i18n } = useLingui();
  // ── Local state ───────────────────────────────────────────────────
  const [filters, setFilters] = React.useState<ChatSyncFilters>(
    initialFilters ?? DEFAULT_FILTERS,
  );
  const [customAfter, setCustomAfter] = React.useState("");
  const [customBefore, setCustomBefore] = React.useState("");
  const [advancedExpanded, setAdvancedExpanded] = React.useState(false);

  // ── Server queries (provider-conditional) ─────────────────────────
  const teams = useChatTeams(provider === "teams" && open);
  const spaces = useChatSpaces(provider === "google_chat" && open);

  // ── Mutations ─────────────────────────────────────────────────────
  const previewMut = useChatPreviewSync();
  const triggerMut = useChatSyncTrigger();
  // Status probe — when an active sync is already running for this
  // provider+account, the BE reports it here and V1 disables the
  // filter UI to prevent double-triggers (one job per connection).
  const status = useChatSyncStatus(provider, connectionKey);
  const hasActiveSync =
    !!status &&
    !!status.job_status &&
    !["completed", "failed", "cancelled"].includes(status.job_status);

  const customRangeError =
    filters.time_range === "custom"
      ? validateCustomRange(customAfter, customBefore, i18n)
      : null;

  const liveFilters: ChatSyncFilters = React.useMemo(
    () => ({
      ...filters,
      custom_start_date:
        filters.time_range === "custom" ? customAfter : undefined,
      custom_end_date:
        filters.time_range === "custom" ? customBefore : undefined,
    }),
    [filters, customAfter, customBefore],
  );

  useDebouncedEffect(
    () => {
      if (filters.time_range === "custom" && (!customAfter || !customBefore)) {
        return;
      }
      if (customRangeError) return;
      previewMut.mutate({ provider, filters: liveFilters, connectionKey });
    },
    [filters, customAfter, customBefore, customRangeError, provider, connectionKey],
    PREVIEW_DEBOUNCE_MS,
  );

  const previewData = previewMut.data;
  const previewing = previewMut.isPending;
  const matchingCount = previewData?.estimated_conversations ?? null;

  const syncDisabled =
    triggerMut.isPending ||
    previewing ||
    hasActiveSync ||
    customRangeError !== null ||
    matchingCount === 0 ||
    matchingCount === null;

  const handleSync = async () => {
    try {
      const res = await triggerMut.mutateAsync({
        provider,
        filters: liveFilters,
        connectionKey,
      });
      toast.success(i18n._(msg`Sincronizzazione chat avviata`));
      onSyncStarted?.(res.job_id ?? null);
      onOpenChange(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : i18n._(msg`errore`);
      toast.error(
        i18n._(msg`Impossibile avviare la sincronizzazione: ${reason}`),
      );
    }
  };

  // ── Provider-specific togglers ─────────────────────────────────────
  const toggleTeam = (teamId: string): void => {
    const set = new Set(filters.teams_ids ?? []);
    set.has(teamId) ? set.delete(teamId) : set.add(teamId);
    setFilters({ ...filters, teams_ids: Array.from(set) });
  };

  const toggleChannel = (channelId: string): void => {
    const set = new Set(filters.channel_ids ?? []);
    set.has(channelId) ? set.delete(channelId) : set.add(channelId);
    setFilters({ ...filters, channel_ids: Array.from(set) });
  };

  const toggleSpace = (spaceId: string): void => {
    const set = new Set(filters.space_ids ?? []);
    set.has(spaceId) ? set.delete(spaceId) : set.add(spaceId);
    setFilters({ ...filters, space_ids: Array.from(set) });
  };

  const toggleSpaceType = (t: GoogleChatSpaceType): void => {
    const set = new Set(filters.space_types ?? []);
    set.has(t) ? set.delete(t) : set.add(t);
    setFilters({ ...filters, space_types: Array.from(set) });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        ariaTitle={i18n._(msg`Filtri di sincronizzazione chat`)}
        className="flex w-[min(440px,90vw)] flex-col sm:max-w-none"
      >
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
              <MessageSquare className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display font-semibold text-[15px] text-ink-1">
                <Trans>Sincronizza conversazioni</Trans>
              </h2>
              <p className="truncate text-[11.5px] text-ink-3">
                {displayName ?? PROVIDER_LABEL[provider]}
                {connectionKey ? ` · ${connectionKey}` : ""}
              </p>
            </div>
          </div>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto px-5 py-4">
          {hasActiveSync ? (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-2 text-[12px] text-ink-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-ando-orange" />
              <Trans>
                Sincronizzazione già in corso — i filtri sono bloccati finché non
                completa.
              </Trans>
            </div>
          ) : null}
          <div
            className={cn(
              "flex flex-col gap-5",
              hasActiveSync && "pointer-events-none opacity-50",
            )}
          >
            {/* ── Time range ──────────────────────────────────── */}
            <section className="flex flex-col gap-1.5">
              <SectionLabel>
                <Trans>Intervallo temporale</Trans>
              </SectionLabel>
              <div className="grid grid-cols-2 gap-1.5">
                <RangeChip
                  active={filters.time_range === "7d"}
                  onClick={() => setFilters({ ...filters, time_range: "7d" })}
                >
                  <Trans>Ultimi 7 giorni</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "30d"}
                  onClick={() => setFilters({ ...filters, time_range: "30d" })}
                >
                  <Trans>Ultimi 30 giorni</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "90d"}
                  onClick={() => setFilters({ ...filters, time_range: "90d" })}
                >
                  <Trans>Ultimi 3 mesi</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "custom"}
                  onClick={() => setFilters({ ...filters, time_range: "custom" })}
                >
                  <Trans>Personalizzato</Trans>
                </RangeChip>
              </div>
              {filters.time_range === "custom" ? (
                <div className="mt-1 flex flex-col gap-1.5">
                  <div className="flex gap-1.5">
                    <DateInput
                      label={i18n._(msg`Dal`)}
                      value={customAfter}
                      onChange={setCustomAfter}
                    />
                    <DateInput
                      label={i18n._(msg`Al`)}
                      value={customBefore}
                      onChange={setCustomBefore}
                    />
                  </div>
                  {customRangeError ? (
                    <p className="text-[11px] text-rose-500">
                      {customRangeError}
                    </p>
                  ) : (
                    <p className="text-[11px] text-ink-3">
                      <Trans>Fino a {MAX_CUSTOM_RANGE_DAYS} giorni.</Trans>
                    </p>
                  )}
                </div>
              ) : null}
            </section>

            {/* ── Chat types (Teams / Slack) ──────────────────── */}
            {(provider === "teams" || provider === "slack") ? (
              <section className="flex flex-col gap-1.5">
                <SectionLabel>
                  <Trans>Tipi di chat</Trans>
                </SectionLabel>
                <div className="grid grid-cols-2 gap-1.5">
                  <CheckboxChip
                    checked={!!filters.include_private_chats}
                    onChange={(v) =>
                      setFilters({ ...filters, include_private_chats: v })
                    }
                    icon={<MessageSquare className="h-3 w-3" />}
                    label={i18n._(msg`Chat private e di gruppo`)}
                  />
                  <CheckboxChip
                    checked={!!filters.include_channels}
                    onChange={(v) =>
                      setFilters({ ...filters, include_channels: v })
                    }
                    icon={<Hash className="h-3 w-3" />}
                    label={i18n._(msg`Canali`)}
                  />
                </div>
              </section>
            ) : null}

            {/* ── Teams: teams + channels picker ──────────────── */}
            {provider === "teams" && teams.data ? (
              <>
                <section className="flex flex-col gap-1.5">
                  <SectionLabel>
                    <Trans>Team</Trans>
                  </SectionLabel>
                  {teams.data.teams.length === 0 ? (
                    <p className="text-[11.5px] text-ink-3">
                      <Trans>Nessun team trovato per questo account.</Trans>
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {teams.data.teams.map((t) => (
                        <EntityChip
                          key={t.id}
                          icon={<Users className="h-3 w-3" />}
                          label={t.name}
                          active={
                            filters.teams_ids?.includes(t.id) ?? false
                          }
                          onClick={() => toggleTeam(t.id)}
                        />
                      ))}
                    </div>
                  )}
                </section>
                {teams.data.channels.length > 0 ? (
                  <section className="flex flex-col gap-1.5">
                    <SectionLabel>
                      <Trans>Canali</Trans>
                    </SectionLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {teams.data.channels.slice(0, 30).map((c) => (
                        <EntityChip
                          key={c.id}
                          icon={<Hash className="h-3 w-3" />}
                          label={c.name}
                          active={
                            filters.channel_ids?.includes(c.id) ?? false
                          }
                          onClick={() => toggleChannel(c.id)}
                        />
                      ))}
                    </div>
                    {teams.data.channels.length > 30 ? (
                      <p className="text-[10.5px] text-ink-3">
                        <Trans>
                          + {teams.data.channels.length - 30} altri canali —
                          seleziona il team per includerli tutti.
                        </Trans>
                      </p>
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : null}

            {/* ── Google Chat: space types + spaces ────────────── */}
            {provider === "google_chat" && spaces.data ? (
              <>
                <section className="flex flex-col gap-1.5">
                  <SectionLabel>
                    <Trans>Tipi di spazio</Trans>
                  </SectionLabel>
                  <div className="flex flex-wrap gap-1.5">
                    <SpaceTypeChip
                      type="SPACE"
                      active={filters.space_types?.includes("SPACE") ?? false}
                      onClick={() => toggleSpaceType("SPACE")}
                    >
                      <Trans>Spazi</Trans>
                    </SpaceTypeChip>
                    <SpaceTypeChip
                      type="GROUP_CHAT"
                      active={
                        filters.space_types?.includes("GROUP_CHAT") ?? false
                      }
                      onClick={() => toggleSpaceType("GROUP_CHAT")}
                    >
                      <Trans>Chat di gruppo</Trans>
                    </SpaceTypeChip>
                    <SpaceTypeChip
                      type="DIRECT_MESSAGE"
                      active={
                        filters.space_types?.includes("DIRECT_MESSAGE") ??
                        false
                      }
                      onClick={() => toggleSpaceType("DIRECT_MESSAGE")}
                    >
                      <Trans>Messaggi diretti</Trans>
                    </SpaceTypeChip>
                  </div>
                </section>
                {spaces.data.spaces.length > 0 ? (
                  <section className="flex flex-col gap-1.5">
                    <SectionLabel>
                      <Trans>Spazi</Trans>
                    </SectionLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {spaces.data.spaces.slice(0, 30).map((s) => (
                        <EntityChip
                          key={s.id}
                          icon={<Hash className="h-3 w-3" />}
                          label={s.name}
                          active={
                            filters.space_ids?.includes(s.id) ?? false
                          }
                          onClick={() => toggleSpace(s.id)}
                        />
                      ))}
                    </div>
                    {spaces.data.spaces.length > 30 ? (
                      <p className="text-[10.5px] text-ink-3">
                        <Trans>
                          + {spaces.data.spaces.length - 30} altri spazi.
                        </Trans>
                      </p>
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : null}

            {/* ── Advanced ────────────────────────────────────── */}
            <section className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => setAdvancedExpanded((s) => !s)}
                className="flex items-center gap-1.5 text-left"
              >
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-ink-3 transition-transform",
                    advancedExpanded ? "rotate-0" : "-rotate-90",
                  )}
                />
                <span className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
                  <Trans>Avanzate</Trans>
                </span>
              </button>
              {advancedExpanded ? (
                <div className="ml-5 flex flex-col gap-2.5">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] text-ink-3">
                      <Trans>Numero massimo di conversazioni</Trans>
                    </label>
                    <StyledSelect
                      value={filters.max_conversations}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          max_conversations: Number(e.target.value),
                        })
                      }
                      className="rounded-md border border-hairline bg-white/55 px-2.5 py-1.5 text-[12px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
                    >
                      {MAX_CONV_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </StyledSelect>
                  </div>
                  {provider === "google_chat" ? (
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-ink-3">
                        <Trans>Esclusione spazi inattivi</Trans>
                      </label>
                      <StyledSelect
                        value={filters.exclude_inactive_days ?? 0}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            exclude_inactive_days:
                              Number(e.target.value) || undefined,
                          })
                        }
                        className="rounded-md border border-hairline bg-white/55 px-2.5 py-1.5 text-[12px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
                      >
                        {INACTIVE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {i18n._(o.label)}
                          </option>
                        ))}
                      </StyledSelect>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            {provider === "google_chat" ? (
              <GoogleMeetTranscriptImport connectionKey={connectionKey} />
            ) : null}

            {/* ── Preview ────────────────────────────────────── */}
            <section className="rounded-lg border border-hairline bg-cream/40 px-3 py-2">
              <div className="flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-ink-3" />
                <p className="flex-1 text-[12px] text-ink-2">
                  {previewing ? (
                    <span className="inline-flex items-center gap-1 text-ink-3">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <Trans>Calcolo…</Trans>
                    </span>
                  ) : matchingCount !== null ? (
                    <>
                      <span className="font-semibold text-ink-1 tabular-nums">
                        {matchingCount.toLocaleString("it-IT")}
                      </span>{" "}
                      <span className="text-ink-3">
                        <Trans>conversazioni</Trans>
                        {previewData?.estimated_messages
                          ? ` · ${previewData.estimated_messages.toLocaleString("it-IT")} msg`
                          : ""}
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-3">
                      <Trans>Anteprima</Trans>
                    </span>
                  )}
                </p>
                <button
                  type="button"
                  aria-label={i18n._(msg`Aggiorna anteprima`)}
                  onClick={() =>
                    previewMut.mutate({
                      provider,
                      filters: liveFilters,
                      connectionKey,
                    })
                  }
                  className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-black/[0.04] hover:text-ink-1"
                >
                  <RefreshCw className="h-3 w-3" />
                </button>
              </div>
            </section>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <button
            type="button"
            disabled={syncDisabled}
            onClick={handleSync}
            className={cn(
              "inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium text-[13px]",
              "bg-ando-orange text-white hover:bg-ando-orange/90",
              "disabled:cursor-not-allowed disabled:bg-ink-3/30 disabled:text-ink-3",
            )}
          >
            {triggerMut.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Filter className="h-3.5 w-3.5" />
            )}
            {triggerMut.isPending
              ? i18n._(msg`Sincronizzando…`)
              : matchingCount !== null && matchingCount > 0
                ? i18n._(
                    msg`Sincronizza ${matchingCount.toLocaleString("it-IT")} conversazioni`,
                  )
                : i18n._(msg`Sincronizza`)}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
ChatSyncFiltersSheet.displayName = "ChatSyncFiltersSheet";

// ──────────────────────────────────────────────────────────────────────
// Sub-components (DRY with Email sheet)
// ──────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
    {children}
  </span>
);

const RangeChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center justify-center rounded-md border px-2.5 py-1.5 font-medium text-[12px]",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    {children}
  </button>
);

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
    <label className="text-[10.5px] text-ink-3">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-hairline bg-white/55 px-2 py-1 text-[12px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
    />
  </div>
);

const CheckboxChip: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
}> = ({ checked, onChange, icon, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    className={cn(
      "inline-flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 font-medium text-[12px]",
      checked
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    <span className={cn(checked ? "text-ando-orange" : "text-ink-3")}>
      {icon}
    </span>
    {label}
  </button>
);

const EntityChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    {icon}
    <span className="max-w-[140px] truncate">{label}</span>
  </button>
);

const SpaceTypeChip: React.FC<{
  type: GoogleChatSpaceType;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center justify-center rounded-md border px-2.5 py-1.5 font-medium text-[12px]",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    {children}
  </button>
);
